import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Input, Radio, Tooltip, Upload } from 'antd';
import { CircleHelp, FileText, Lightbulb, PenLine, Plus, ScrollText } from 'lucide-react';
import { useId, useRef, useState } from 'react';
import { createProjectDraft } from '@/api/projectApi';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import { openAuthDialog } from '@/entities/session/model/sessionSlice';
import { useAuthedQueryEnabled } from '@/entities/session/model/useAuthedQueryEnabled';
import { openCreateProjectDialog } from '@/features/create-project/model/createProjectSlice';
import { stageCreationSession, readCreationSession, } from '@/features/create-project/model/creationSession';
import { notifyApiError, notifyApiWarning } from '@/shared/api/httpClient';
import { createId } from '@/shared/lib/id/createId';
import '@/features/create-project/ui/styles/creation-entry.css';
const MODES = [
    { value: 'inspiration', title: '一句话灵感', icon: Lightbulb },
    { value: 'non_standard', title: '小说剧本', icon: PenLine },
    { value: 'standard', title: '标准剧本', icon: ScrollText },
    { value: 'blank', title: '新建项目', icon: FileText },
];
/** 首页创作入口，下一步之前仅灵感会生成隐藏草稿。 */
export function CreationEntry() {
    const entryId = useId();
    const dispatch = useAppDispatch();
    const isAuthed = useAuthedQueryEnabled();
    const ownerId = useAppSelector((state) => state.session.user?.userId);
    const [mode, setMode] = useState('inspiration');
    const [inspiration, setInspiration] = useState('');
    const [strategy, setStrategy] = useState('strict');
    const [files, setFiles] = useState({
        standard: [],
        non_standard: [],
    });
    const [busy, setBusy] = useState(false);
    const requestRef = useRef(null);
    const submittingRef = useRef(false);
    const modeSessionsRef = useRef({});
    const fileMode = mode === 'standard' || mode === 'non_standard' ? mode : null;
    async function proceed() {
        if (submittingRef.current)
            return;
        if (!isAuthed || ownerId === undefined) {
            dispatch(openAuthDialog());
            return;
        }
        if (mode === 'inspiration' && !inspiration.trim()) {
            notifyApiWarning('请输入一句话灵感');
            return;
        }
        const chosenFiles = fileMode
            ? files[fileMode].flatMap((file) => (file.originFileObj ? [file.originFileObj] : []))
            : [];
        if (fileMode && chosenFiles.length === 0) {
            notifyApiWarning('请选择至少一个 Word 文件');
            return;
        }
        submittingRef.current = true;
        setBusy(true);
        try {
            const previousKey = modeSessionsRef.current[mode];
            const previous = readCreationSession(previousKey ?? null, ownerId);
            if (mode !== 'inspiration' &&
                previous?.novelGenerationStrategy === strategy &&
                previous.files.length === chosenFiles.length &&
                previous.files.every((file, index) => file === chosenFiles[index])) {
                dispatch(openCreateProjectDialog(previousKey));
                return;
            }
            let draftId;
            let autoStartPlanTaskId;
            if (mode === 'inspiration') {
                if (requestRef.current?.inspiration !== inspiration.trim() ||
                    requestRef.current.ownerId !== ownerId) {
                    requestRef.current = {
                        inspiration: inspiration.trim(),
                        ownerId,
                        key: createId(),
                    };
                }
                if (requestRef.current.sessionKey &&
                    readCreationSession(requestRef.current.sessionKey, ownerId)) {
                    dispatch(openCreateProjectDialog(requestRef.current.sessionKey));
                    return;
                }
                if (requestRef.current.sessionKey)
                    requestRef.current = {
                        inspiration: inspiration.trim(),
                        ownerId,
                        key: createId(),
                    };
                const flow = await createProjectDraft({
                    requestKey: requestRef.current.key,
                    inspiration: inspiration.trim(),
                });
                draftId = flow.projectId;
                const task = flow.tasks.find((item) => item.taskType === 'inspiration_plan' && item.status === 0);
                autoStartPlanTaskId = task?.id;
            }
            const sessionKey = stageCreationSession({
                ownerId,
                mode,
                files: chosenFiles,
                inspiration: inspiration.trim(),
                novelGenerationStrategy: strategy,
                draftId,
                autoStartPlanTaskId,
            });
            modeSessionsRef.current[mode] = sessionKey;
            if (mode === 'inspiration' && requestRef.current)
                requestRef.current.sessionKey = sessionKey;
            dispatch(openCreateProjectDialog(sessionKey));
        }
        catch (error) {
            notifyApiError(error, { fallback: '暂时无法开始创作，请重试' });
        }
        finally {
            submittingRef.current = false;
            setBusy(false);
        }
    }
    return (_jsxs("section", { className: "creation-entry", "aria-labelledby": `${entryId}-title`, children: [_jsxs("div", { className: "creation-entry__heading", children: [_jsx("h2", { id: `${entryId}-title`, children: "\u5F00\u59CB\u4F60\u7684\u521B\u4F5C" }), _jsx("p", { children: "\u4ECE\u7075\u611F\u6216\u5267\u672C\u51FA\u53D1\uFF0C\u521B\u5EFA\u4F60\u7684\u5F71\u6E38\u9879\u76EE" })] }), _jsx(Radio.Group, { className: "creation-entry__modes", value: mode, disabled: busy, onChange: (event) => {
                    setMode(event.target.value);
                }, children: MODES.map(({ value, title, icon: Icon }) => (_jsxs(Radio.Button, { value: value, children: [_jsx(Icon, { size: 18, "aria-hidden": "true" }), title] }, value))) }), mode === 'inspiration' && (_jsxs("div", { className: "creation-entry__input", children: [_jsx(Input.TextArea, { id: `${entryId}-inspiration`, "aria-label": "\u4E00\u53E5\u8BDD\u7075\u611F", value: inspiration, disabled: busy, onChange: (event) => {
                            setInspiration(event.target.value);
                        }, maxLength: 500, showCount: true, autoSize: { minRows: 3, maxRows: 6 }, placeholder: "\u4F8B\u5982\uFF1A\u4E00\u4E2A\u5931\u53BB\u8BB0\u5FC6\u7684\u4FA6\u63A2\u53D1\u73B0\u6574\u5EA7\u57CE\u5E02\u6BCF\u5929\u51CC\u6668\u90FD\u4F1A\u91CD\u7F6E\u2026" }), _jsx("p", { children: "\u4E3A\u4F60\u7684\u7075\u611F\u751F\u6210\u4E09\u4E2A\u5267\u60C5\u65B9\u6848" })] })), fileMode && (_jsx("div", { className: "creation-entry__input", children: _jsxs(Upload.Dragger, { multiple: true, accept: ".doc,.docx", disabled: busy, fileList: files[fileMode], beforeUpload: (file) => {
                        if (!/\.docx?$/i.test(file.name)) {
                            notifyApiWarning('仅支持 .doc 或 .docx 文件');
                            return Upload.LIST_IGNORE;
                        }
                        return false;
                    }, onChange: ({ fileList }) => {
                        setFiles((previous) => ({ ...previous, [fileMode]: fileList }));
                    }, children: [_jsx(Plus, { size: 24 }), _jsx("p", { children: fileMode === 'standard' ? '上传标准剧本' : '上传小说剧本' }), _jsx("small", { children: fileMode === 'standard'
                                ? '拖入或点击选择 Word 文件，多个文件按列表顺序合并'
                                : '把小说或大纲拖到此处，或点击选择文档' })] }) })), mode === 'non_standard' && (_jsxs("div", { className: "creation-entry__strategy", children: [_jsxs("span", { className: "creation-entry__strategy-label", children: ["\u751F\u6210\u65B9\u5F0F", _jsx(Tooltip, { title: "\u4E25\u683C\u6309\u7167\u5267\u672C\u751F\u6210\uFF1A\u4FDD\u7559\u539F\u6709\u5267\u60C5\u8D70\u5411\u3002\u8C03\u6574\u5267\u60C5\u751F\u6210\u5F71\u6E38\u5267\u60C5\uFF1A\u4FDD\u7559\u539F\u8457\u4E3B\u7EBF\uFF0C\u589E\u52A0\u4E24\u6761\u7531\u73A9\u5BB6\u9009\u62E9\u8FDB\u5165\u7684\u5206\u652F\u3002", children: _jsx("button", { type: "button", className: "creation-entry__strategy-help", "aria-label": "\u4E86\u89E3\u4E24\u79CD\u751F\u6210\u65B9\u5F0F\u7684\u533A\u522B", children: _jsx(CircleHelp, { size: 16, "aria-hidden": "true" }) }) })] }), _jsxs(Radio.Group, { value: strategy, disabled: busy, onChange: (event) => {
                            setStrategy(event.target.value);
                        }, children: [_jsx(Radio, { value: "strict", children: "\u4E25\u683C\u6309\u7167\u5267\u672C\u751F\u6210" }), _jsx(Radio, { value: "branch_adaptation", children: "\u8C03\u6574\u5267\u60C5\u751F\u6210\u5F71\u6E38\u5267\u60C5" })] })] })), mode === 'blank' && (_jsx("p", { className: "creation-entry__blank", children: "\u4ECE\u7A7A\u767D\u8109\u7EDC\u56FE\u5F00\u59CB\uFF0C\u8FDB\u5165\u540E\u624B\u52A8\u6DFB\u52A0\u7AE0\u8282\u3001\u5267\u60C5\u8282\u70B9\u548C\u5177\u4F53\u5185\u5BB9\u3002" })), _jsx("div", { className: "creation-entry__actions", children: _jsx(Button, { type: "primary", loading: busy, onClick: () => {
                        void proceed();
                    }, children: mode === 'inspiration' ? '生成方案 · 1 积分' : '创建项目' }) })] }));
}
